/**
 * ConsoleManager - interactive developer console with command handlers and history.
 */

import { BaseInputHandler } from './InputManager.js';
import { ObjectTracker } from './ObjectTracker.js';


import type { MouseButton } from './InputManager.js';

export interface ConsoleCommandInfo {
  name: string;
  description: string;
  method: Function;
}

const CONSOLE_COMMANDS_SYMBOL = Symbol('consoleCommands');

/**
 * Decorator to register a console command.
 * @param name - The command name
 * @param description - Human-readable description of what the command does
 */
export function ConsoleCommand(name: string, description: string) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    target.constructor[CONSOLE_COMMANDS_SYMBOL] ??= new Map<string, ConsoleCommandInfo>();

    const commands = target.constructor[CONSOLE_COMMANDS_SYMBOL] as Map<string, ConsoleCommandInfo>;
    commands.set(name.toLowerCase(), {
      name,
      description,
      method: descriptor.value
    });
  };
}

export class ConsoleManager extends BaseInputHandler {
  private abortController = new AbortController();
  private container: HTMLElement | null = null;
  private consoleWrapper: HTMLElement | null = null;
  private isConsoleVisible: boolean = false;
  private handlers: any[] = [];
  private executeCommand: (cmd: string) => void;

  constructor() {
    super();
    ObjectTracker.track(this);
    this.executeCommand = () => {};
  }

  public executeConsoleCommand(cmd: string): void {
    if (this.executeCommand) {
      this.executeCommand(cmd);
    }
  }

  public destroy(): void {
    if (this.container) {
      this.container.remove();
    }
    this.inputManager?.removeInputHandler(this);
    this.abortController.abort();
  }

  public addCommandHandler(handler: any) {
    this.handlers.push(handler);
  }

  public removeCommandHandler(handler: any) {
    this.handlers = this.handlers.filter((h) => h !== handler);
  }

  private openHelpPage(): void {
    const commands = new Map<string, string>();

    // Built-in commands
    commands.set('help', 'Show this help page in browser');
    commands.set('clear', 'Clear the console output');
    commands.set('version', 'Show console version');

    // Collect decorator-registered commands from all handlers
    for (const handler of this.handlers) {
      const handlerCommands = handler.constructor[CONSOLE_COMMANDS_SYMBOL] as Map<string, ConsoleCommandInfo>;
      if (handlerCommands) {
        for (const [name, info] of handlerCommands) {
          commands.set(name, info.description);
        }
      }
    }

    // Sort commands alphabetically
    const sortedCommands = Array.from(commands.entries()).sort();

    // Generate command list HTML with proper HTML encoding
    const commandsHtml = sortedCommands.map(([name, description]) => {
      // Escape HTML characters in description
      const escapedDescription = description
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');


      return `<div class="command">
        <div class="command-name">${name}</div>
        <div class="command-description">${escapedDescription}</div>
      </div>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Genesys Console Commands</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
            line-height: 1.6;
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
            background: #1a1a1a;
            color: #e0e0e0;
        }
        .header {
            text-align: center;
            margin-bottom: 40px;
            padding: 20px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 12px;
        }
        h1 {
            margin: 0;
            font-size: 2.5em;
            font-weight: 700;
        }
        .subtitle {
            margin: 10px 0 0 0;
            opacity: 0.9;
            font-size: 1.1em;
        }
        .commands-container {
            margin-bottom: 40px;
            background: #2a2a2a;
            border-radius: 12px;
            padding: 25px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
        }
        .command-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
            gap: 15px;
        }
        .command {
            background: #333;
            border-radius: 8px;
            padding: 20px;
            border-left: 4px solid #4fc3f7;
            transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .command:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 12px rgba(79, 195, 247, 0.2);
        }
        .command-name {
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            font-size: 1.2em;
            font-weight: bold;
            color: #81c784;
            margin-bottom: 8px;
        }
        .command-description {
            color: #b0b0b0;
            font-size: 0.95em;
            line-height: 1.5;
        }
        .usage {
            background: #1e1e1e;
            border-radius: 6px;
            padding: 15px;
            margin: 30px 0;
            border-left: 4px solid #ff9800;
        }
        .usage h3 {
            margin: 0 0 10px 0;
            color: #ff9800;
        }
        .usage p {
            margin: 5px 0;
            font-family: monospace;
        }
        @media (max-width: 768px) {
            .command-grid {
                grid-template-columns: 1fr;
            }
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>🎮 Genesys Console Commands</h1>
        <p class="subtitle">Interactive developer console reference</p>
    </div>
    
    <div class="usage">
        <h3>💡 How to use the console:</h3>
        <p>• Press <strong>\`</strong> (backtick) to open/close the console</p>
        <p>• Type any command name and press Enter</p>
        <p>• Use arrow keys to navigate command history</p>
        <p>• Commands are case-insensitive</p>
    </div>

    <div class="commands-container">
        <div class="command-grid">
            ${commandsHtml}
        </div>
    </div>
    
    
    <div style="text-align: center; margin-top: 40px; opacity: 0.6;">
        <p>Generated at ${new Date().toLocaleString()}</p>
    </div>
</body>
</html>`;


    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }

  public override handleKeyDown(e: KeyboardEvent): boolean {
    /*
    if (process.env.NODE_ENV === 'production') {
      return false;
    }
    */
    // Prefer physical key (`e.code`) so layouts where ` doesn't produce e.key === '`' still work (e.g. many ES/LATAM keyboards).
    if (!e.repeat && (e.code === 'Backquote' || e.key === '`')) {
      this.toggleConsole();
      return true;
    }
    return this.isConsoleVisible;
  }

  public override handleKeyUp(e: KeyboardEvent): boolean {
    return this.isConsoleVisible;
  }

  public override handleMouseDown(button: MouseButton, e: MouseEvent): boolean {
    return this.isConsoleVisible;
  }

  public override handleMouseUp(button: MouseButton, e: MouseEvent): boolean {
    return this.isConsoleVisible;
  }

  public override handleMouseMove(e: MouseEvent): boolean {
    return this.isConsoleVisible;
  }

  public override handleMouseClick(button: MouseButton, e: MouseEvent): boolean {
    return this.isConsoleVisible;
  }

  //====================================================================================================

  private createConsole(parent: HTMLElement): { container: HTMLElement, consoleWrapper: HTMLElement } {
    // Create wrapper
    const container = document.createElement('div');
    container.style.width = '50%';
    container.style.position = 'absolute';
    container.style.top = '10px';
    container.style.left = '50%';
    container.style.transform = 'translateX(-50%)';
    container.style.zIndex = '100'; // Make sure it appears above the canvas
    container.style.pointerEvents = 'auto'; // Make sure it receives clicks

    // Create console UI wrapper
    const consoleWrapper = document.createElement('div');
    consoleWrapper.style.display = 'none'; // Hidden by default
    consoleWrapper.style.width = '100%'; // Ensure wrapper takes full width of container
    container.appendChild(consoleWrapper);

    // Create output area
    const output = document.createElement('div');
    output.id = 'console-output';
    output.style.cssText = `
      width: calc(100% - 20px);
      height: 500px;
      overflow-y: auto;
      background-color: #000;
      color: #0f0;
      padding: 10px;
      font-size: 14px;
      margin-bottom: 10px;
      white-space: pre-wrap;
      border-radius: 4px;
      font-family: monospace;
      box-sizing: border-box;
    `;
    consoleWrapper.appendChild(output);

    // Create input field
    const input = document.createElement('input');
    input.id = 'console-input';
    input.type = 'text';
    input.placeholder = 'Enter command...';
    input.autocomplete = 'off';
    input.style.cssText = `
      width: calc(100% - 20px);
      padding: 10px;
      background: #222;
      color: #fff;
      border: 1px solid #333;
      border-radius: 4px;
      font-size: 14px;
      font-family: monospace;
      box-sizing: border-box;
    `;
    consoleWrapper.appendChild(input);

    // Command logic
    const history: string[] = [];
    let historyIndex = -1;

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const cmd = input.value.trim();
        if (cmd) {
          logCommand(cmd);
          executeCommand(cmd);
          history.push(cmd);
          historyIndex = history.length;
          input.value = '';
        }
      } else if (e.key === 'ArrowUp') {
        if (historyIndex > 0) {
          input.value = history[--historyIndex];
        }
      } else if (e.key === 'ArrowDown') {
        if (historyIndex < history.length - 1) {
          input.value = history[++historyIndex];
        } else {
          input.value = '';
          historyIndex = history.length;
        }
      }
    }, { signal: this.abortController.signal });

    function getAllPropertyNames(obj: any): string[] {
      const props = new Set<string>();

      while (obj && obj !== Object.prototype) {
        Object.getOwnPropertyNames(obj).forEach(p => props.add(p));
        obj = Object.getPrototypeOf(obj);
      }

      return Array.from(props);
    }

    function logCommand(cmd: string) {
      const line = document.createElement('div');
      line.textContent = '> ' + cmd;
      output.appendChild(line);
      output.scrollTop = output.scrollHeight;
    }

    const executeCommand = (cmd: string) => {
      let response: string | undefined;

      const args = cmd.split(' ');
      const commandName = args.shift()?.toLowerCase() ?? '';
      if (!commandName) {
        return;
      }

      // First, try to find decorator-registered commands
      let handled = this.handlers.some(handler => {
        const commands = handler.constructor[CONSOLE_COMMANDS_SYMBOL] as Map<string, ConsoleCommandInfo>;
        if (commands && commands.has(commandName)) {
          const commandInfo = commands.get(commandName)!;
          response = commandInfo.method.call(handler, ...args);
          return true;
        }
        return false;
      });

      if (!handled) {
        // Try console variable handlers for dynamic commands
        for (const handler of this.handlers) {
          if (handler.handleDynamicCommand && typeof handler.handleDynamicCommand === 'function') {
            const dynamicResponse = handler.handleDynamicCommand(commandName, args);
            if (dynamicResponse !== null) {
              response = dynamicResponse;
              handled = true;
              break;
            }
          }
        }
      }

      if (!handled) {
        switch (cmd) {
          case 'help':
            this.openHelpPage();
            response = 'Help page opened in new tab';
            break;
          case 'clear':
            output.innerHTML = '';
            return;
          case 'version':
            response = 'Console UI v1.0';
            break;
          default:
            response = `Unknown command: ${cmd}`;
        }
      }

      if (response) {
        const resLine = document.createElement('div');
        resLine.textContent = response;
        output.appendChild(resLine);
        output.scrollTop = output.scrollHeight;
      }
    };

    // Store the executeCommand function reference
    this.executeCommand = executeCommand;

    parent.appendChild(container);
    return { container, consoleWrapper };
  }

  private toggleConsole(): void {
    if (!this.container) {
      // const dom = this.inputManager!.options.rendererDomElement;
      const parent = this.inputManager!.options.world.deref()!.options.gameContainer;
      if (!parent) {
        console.warn('[ConsoleManager] Cannot create console in headless mode');
        return;
      }
      const { container, consoleWrapper } = this.createConsole(parent);
      this.container = container;
      this.consoleWrapper = consoleWrapper;
    }
    this.isConsoleVisible = !this.isConsoleVisible;
    this.consoleWrapper!.style.display = this.isConsoleVisible ? 'block' : 'none';
    if (this.isConsoleVisible) {
      if (document.exitPointerLock) {
        document.exitPointerLock();
      }
      setTimeout(() => {
        // Focus on the input field when console is shown
        const inputField = document.getElementById('console-input') as HTMLInputElement;
        if (inputField) {
          inputField.focus();
          inputField.value = '';
        }
      }, 100);
    }
  }
}
